/* Small, event-driven web lenses. Technique references:
   https://github.com/Simonstorms/lore-glass (GlassSurface)
   https://aave.com/design/building-glass-for-the-web
   Independent SDF map implementation; no React, renderer capture or animation loop. */
(()=>{
 function lensPixels(width,height,radius){
  const w=Math.max(4,Math.min(512,Math.round(width))),h=Math.max(4,Math.min(128,Math.round(height)));
  const r=Math.max(0,Math.min(Number.isFinite(radius)?radius:0,w/2,h/2)),rim=Math.min(10,h*.18),mw=Math.min(256,Math.max(64,Math.ceil(w*1.25))),mh=Math.min(160,Math.max(64,Math.ceil(h*1.25)));
  const data=new Uint8ClampedArray(mw*mh*4),hw=w/2,hh=h/2;
  for(let y=0;y<mh;y++)for(let x=0;x<mw;x++){
   const px=(x+.5)*w/mw-hw,py=(y+.5)*h/mh-hh,qx=Math.abs(px)-(hw-r),qy=Math.abs(py)-(hh-r);
   const ox=Math.max(qx,0),oy=Math.max(qy,0),len=Math.hypot(ox,oy),sd=len+Math.min(Math.max(qx,qy),0)-r;
   let nx=0,ny=0,bend=0;
   if(sd<0&&sd>-rim){
    const t=-sd/rim;bend=Math.sin(Math.PI*t)*(1-t)*.85;
    if(len){nx=ox/len;ny=oy/len}else if(qx>qy)nx=1;else ny=1;
    nx*=Math.sign(px);ny*=Math.sign(py);
   }
   const i=(y*mw+x)*4;data[i]=Math.round(128+126*nx*bend);data[i+1]=Math.round(128+126*ny*bend);data[i+2]=128;data[i+3]=255;
  }
  return {width:mw,height:mh,data};
 }
 const core={lensPixels};
 if(typeof module==='object'&&module.exports){module.exports=core;return}
 window.VorugGlassMaterial=core;
 const root=document.querySelector('vorug-infinite-collection[data-journey=on][data-ui="2"]');if(!root)return;
 const selector='.vic-shapes,.vic-filters .vic-row,.vic-style-toggle,.vic-actions-toggle,.vic-tools',controller=new AbortController(),signal=controller.signal;
 const quiet=matchMedia('(prefers-reduced-motion: reduce)'),contrast=matchMedia('(prefers-contrast: more)'),opaque=matchMedia('(prefers-reduced-transparency: reduce)'),forced=matchMedia('(forced-colors: active)');
 const rails=[root.querySelector('.vic-shapes'),root.querySelector('[data-filter=color]')].filter(Boolean),touched=new Set(),records=new Map(),cache=new Map();
 const chromium=/(Chrome|Chromium|Edg|OPR)\//.test(navigator.userAgent),canRefract=chromium&&!!window.CSS?.supports?.('backdrop-filter','url(#vic-glass-capability)');
 let frame=0,settle=0,active=null,point=null,pressed=false,geometryFrame=0,sequence=0,defs=null,svg=null;
 const disabled=()=>quiet.matches||contrast.matches||opaque.matches||forced.matches;
 const on=(node,event,fn,options={})=>node.addEventListener(event,fn,{...options,signal});
 function clear(soft=false){
  clearTimeout(settle);settle=0;
  cancelAnimationFrame(frame);frame=0;point=null;pressed=false;
  for(const node of touched){delete node.dataset.engaged;delete node.dataset.glassPressed}
  const reset=()=>{for(const node of touched){node.style.removeProperty('--glass-x');node.style.removeProperty('--glass-y')}touched.clear();settle=0};
  // Let the highlight dim at its last touch position, then return to the resting rim.
  if(soft&&touched.size&&!disabled()&&!document.hidden)settle=setTimeout(reset,220);else reset();
  active=null;
 }
 function illuminate(event,down=false){
  if(disabled()||document.hidden)return;
  const target=event.target.closest?.(selector);if(!target||!root.contains(target))return;
  if((active&&active!==target)||settle)clear();active=target;pressed=pressed||down;
  if(event.pointerType==='touch'&&!pressed)return;
  if(!Number.isFinite(event.clientX)||!Number.isFinite(event.clientY))return;
  point={node:target,x:event.clientX,y:event.clientY};
  if(!frame)frame=requestAnimationFrame(()=>{
   frame=0;if(!point||disabled()||document.hidden)return;const {node,x,y}=point,b=node.getBoundingClientRect();
   if(!b.width||!b.height)return;
   node.style.setProperty('--glass-x',`${Math.round(Math.max(0,Math.min(1,(x-b.left)/b.width))*1000)/10}%`);
   node.style.setProperty('--glass-y',`${Math.round(Math.max(0,Math.min(1,(y-b.top)/b.height))*1000)/10}%`);
   node.dataset.engaged='';if(pressed)node.dataset.glassPressed='';touched.add(node);
  });
 }
 const ns='http://www.w3.org/2000/svg';
 function element(tag,attrs){const e=document.createElementNS(ns,tag);for(const [key,value]of Object.entries(attrs))e.setAttribute(key,String(value));return e}
 function mapURL(w,h,r){
  const key=`${w}|${h}|${r}`;if(cache.has(key)){const value=cache.get(key);cache.delete(key);cache.set(key,value);return value}
  const pixels=lensPixels(w,h,r),canvas=document.createElement('canvas');canvas.width=pixels.width;canvas.height=pixels.height;
  const context=canvas.getContext('2d');if(!context)return null;
  const image=context.createImageData(pixels.width,pixels.height);image.data.set(pixels.data);context.putImageData(image,0,0);
  const value=canvas.toDataURL('image/png');cache.set(key,value);if(cache.size>12)cache.delete(cache.keys().next().value);return value;
 }
 function refresh(){
  geometryFrame=0;
  for(const node of rails){
   const fallback=()=>{node.style.removeProperty('--vic-glass-refraction');delete node.dataset.glassRefraction};
   if(!canRefract||disabled()||document.hidden){fallback();continue}
   const w=Math.round(node.offsetWidth),h=Math.round(node.offsetHeight);
   if(w<4||h<4||w>512||h>128){fallback();continue}
   const radius=parseFloat(getComputedStyle(node).borderTopLeftRadius),r=Math.min(Number.isFinite(radius)?radius:30,w/2,h/2),key=`${w}|${h}|${r}`;
   let record=records.get(node);
   try{
    if(!record||record.key!==key){
     const url=mapURL(w,h,r);if(!url){fallback();continue}
     if(!svg){svg=element('svg',{'aria-hidden':'true',focusable:'false',width:0,height:0});svg.style.position='absolute';svg.style.pointerEvents='none';defs=element('defs',{});svg.append(defs);root.append(svg)}
     record?.filter.remove();const id=`vic-optical-rim-${++sequence}`;
     const filter=element('filter',{id,x:0,y:0,width:1,height:1,filterUnits:'objectBoundingBox',primitiveUnits:'userSpaceOnUse','color-interpolation-filters':'sRGB'});
     filter.append(element('feImage',{href:url,x:0,y:0,width:w,height:h,preserveAspectRatio:'none',result:'map'}),element('feDisplacementMap',{in:'SourceGraphic',in2:'map',scale:Math.min(10,h*.16),xChannelSelector:'R',yChannelSelector:'G'}));
     defs.append(filter);record={key,id,filter};records.set(node,record);
    }
    node.style.setProperty('--vic-glass-refraction',`url("#${record.id}")`);node.dataset.glassRefraction='true';
   }catch{fallback()}
  }
 }
 const schedule=()=>{if(!geometryFrame)geometryFrame=requestAnimationFrame(refresh)};
 const resize=new ResizeObserver(schedule);rails.forEach(node=>resize.observe(node));
 on(root,'pointerdown',event=>illuminate(event,true),{passive:true});
 on(root,'pointermove',event=>illuminate(event),{passive:true});
 on(root,'pointerover',event=>{if(event.pointerType==='mouse'||event.pointerType==='pen')illuminate(event)},{passive:true});
 on(root,'pointerout',event=>{const next=event.relatedTarget;if(active&&(!(next instanceof Node)||!active.contains(next)))clear(true)},{passive:true});
 on(document,'pointerup',()=>clear(true),{passive:true});on(document,'pointercancel',()=>clear(),{passive:true});
 for(const media of [quiet,contrast,opaque,forced])on(media,'change',()=>{clear();schedule()});
 on(root,'vorug:controls-settled',schedule);on(document,'visibilitychange',()=>{if(document.hidden)clear();schedule()});
 on(window,'pagehide',()=>{clear();cancelAnimationFrame(geometryFrame);geometryFrame=0;resize.disconnect()});
 on(window,'pageshow',event=>{if(event.persisted){rails.forEach(node=>resize.observe(node));schedule()}});
 on(root,'vorug:disconnected',()=>{
  clear();cancelAnimationFrame(geometryFrame);resize.disconnect();controller.abort();svg?.remove();cache.clear();
  for(const node of rails){node.style.removeProperty('--vic-glass-refraction');delete node.dataset.glassRefraction}
 },{once:true});
 schedule();
})();
